import { useEffect, useRef, useState } from "react";
import {
  addEntry,
  deleteEntry,
  getSchedule,
  listContent,
  listPlaylists,
  renameSchedule,
  updateEntry,
} from "../lib/resources.ts";
import { DEFAULT_SLIDE_SECONDS } from "../lib/defaults.ts";
import type {
  Content,
  EntryInput,
  Playlist,
  ScheduleDetail,
  ScheduleEntry,
} from "../lib/types.ts";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function timingSummary(e: ScheduleEntry): string {
  const days =
    !e.days || e.days.length === 0
      ? "Every day"
      : e.days.length === 7
        ? "Every day"
        : e.days
            .slice()
            .sort((a, b) => a - b)
            .map((d) => DAY_LABELS[d])
            .join(", ");
  const time = e.startTime ? `${e.startTime}–${e.endTime ?? "end of day"}` : "All day";
  const dates =
    e.validFrom || e.validUntil
      ? ` · ${e.validFrom ?? "…"} → ${e.validUntil ?? "…"}`
      : "";
  return `${days} · ${time}${dates}`;
}

// Edit a schedule's timed entries: add content/playlists with day/time/date
// windows and priority. Higher priority wins on overlap.
export default function ScheduleEditor({
  scheduleId,
  onClose,
  autoFocusName = false,
}: {
  scheduleId: string;
  onClose: () => void;
  /** Set for a just-created schedule: focus and select the name so it can be typed over. */
  autoFocusName?: boolean;
}) {
  const [detail, setDetail] = useState<ScheduleDetail | null>(null);
  const [content, setContent] = useState<Content[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState<ScheduleEntry | "new" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // Runs after `detail` lands, because the input isn't mounted until then.
  useEffect(() => {
    if (autoFocusName && detail) nameRef.current?.select();
  }, [autoFocusName, detail]);

  async function refresh() {
    const d = await getSchedule(scheduleId);
    setDetail(d);
    setName(d.name);
  }

  useEffect(() => {
    (async () => {
      try {
        const [d, c, p] = await Promise.all([
          getSchedule(scheduleId),
          listContent(),
          listPlaylists(),
        ]);
        setDetail(d);
        setName(d.name);
        setContent(c);
        setPlaylists(p);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load schedule");
      }
    })();
  }, [scheduleId]);

  async function saveName() {
    if (detail && name.trim() && name.trim() !== detail.name) {
      await renameSchedule(scheduleId, name.trim());
    }
  }

  async function saveEntry(input: EntryInput, existing: ScheduleEntry | "new") {
    try {
      if (existing === "new") await addEntry(scheduleId, input);
      else await updateEntry(scheduleId, existing.id, input);
      setEditing(null);
      await refresh();
    } catch (e) {
      throw e instanceof Error ? e : new Error("Save failed");
    }
  }

  async function removeEntry(entry: ScheduleEntry) {
    if (!confirm("Remove this entry?")) return;
    await deleteEntry(scheduleId, entry.id);
    await refresh();
  }

  if (!detail) {
    return (
      <section className="admin-page">
        {error ? <div className="banner banner--error">{error}</div> : <p className="muted">Loading…</p>}
        <button className="link-btn" onClick={onClose}>← Back</button>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <button className="link-btn" onClick={onClose}>← Back to schedules</button>
      <div className="page-head">
        <input
          ref={nameRef}
          className="title-input"
          aria-label="Schedule name"
          placeholder="Schedule name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
        />
        <div className="page-actions">
          <button className="btn" onClick={() => setEditing("new")}>
            + Add entry
          </button>
        </div>
      </div>
      <p className="muted">
        {detail.entries.length} {detail.entries.length === 1 ? "entry" : "entries"} ·
        higher priority wins when windows overlap
      </p>

      {error && <div className="banner banner--error">{error}</div>}

      {detail.entries.length === 0 ? (
        <div className="admin-empty">
          <strong>No entries</strong>
          <span>Add content or a playlist with its timing and priority.</span>
        </div>
      ) : (
        <div className="list">
          {detail.entries.map((e) => (
            <div className="entry-row card" key={e.id}>
              <span className="prio" title="priority">{e.priority}</span>
              <div className="entry-main">
                <div className="entry-name">
                  {e.targetName ?? "(missing)"}
                  <span className="badge">{e.entryType}</span>
                  {e.entryType === "content" && e.durationSeconds && (
                    <span className="muted"> · {e.durationSeconds}s</span>
                  )}
                </div>
                <div className="muted entry-timing">{timingSummary(e)}</div>
              </div>
              <div className="list-row-actions">
                <button className="link-btn" onClick={() => setEditing(e)}>Edit</button>
                <button className="link-btn link-btn--danger" onClick={() => removeEntry(e)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EntryForm
          initial={editing === "new" ? null : editing}
          content={content}
          playlists={playlists}
          onCancel={() => setEditing(null)}
          onSave={(input) => saveEntry(input, editing)}
        />
      )}
    </section>
  );
}

// ---- Entry form modal ----

function EntryForm({
  initial,
  content,
  playlists,
  onCancel,
  onSave,
}: {
  initial: ScheduleEntry | null;
  content: Content[];
  playlists: Playlist[];
  onCancel: () => void;
  onSave: (input: EntryInput) => Promise<void>;
}) {
  const [entryType, setEntryType] = useState<"content" | "playlist">(
    initial?.entryType ?? "content",
  );
  const [contentId, setContentId] = useState(initial?.contentId ?? "");
  const [playlistId, setPlaylistId] = useState(initial?.playlistId ?? "");
  const [duration, setDuration] = useState(initial?.durationSeconds ?? DEFAULT_SLIDE_SECONDS);
  const [priority, setPriority] = useState(initial?.priority ?? 0);
  const [days, setDays] = useState<number[]>(initial?.days ?? []);
  const [startTime, setStartTime] = useState(initial?.startTime ?? "");
  const [endTime, setEndTime] = useState(initial?.endTime ?? "");
  const [validFrom, setValidFrom] = useState(initial?.validFrom ?? "");
  const [validUntil, setValidUntil] = useState(initial?.validUntil ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggleDay(d: number) {
    setDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
    );
  }

  async function submit() {
    if (entryType === "content" && !contentId) return setErr("Pick a content item.");
    if (entryType === "playlist" && !playlistId) return setErr("Pick a playlist.");
    if (startTime && !endTime) return setErr("Set an end time (or clear the start time).");

    const input: EntryInput = {
      entryType,
      contentId: entryType === "content" ? contentId : null,
      playlistId: entryType === "playlist" ? playlistId : null,
      durationSeconds: entryType === "content" ? duration : null,
      priority,
      days: days.length ? days : null,
      startTime: startTime || null,
      endTime: endTime || null,
      validFrom: validFrom || null,
      validUntil: validUntil || null,
    };
    setSaving(true);
    setErr(null);
    try {
      await onSave(input);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
      setSaving(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{initial ? "Edit entry" : "Add entry"}</h2>

        <div className="seg">
          <button
            className={"seg-btn" + (entryType === "content" ? " seg-btn--on" : "")}
            onClick={() => setEntryType("content")}
          >
            Single content
          </button>
          <button
            className={"seg-btn" + (entryType === "playlist" ? " seg-btn--on" : "")}
            onClick={() => setEntryType("playlist")}
          >
            Playlist
          </button>
        </div>

        {entryType === "content" ? (
          <label className="field">
            <span>Content</span>
            <select value={contentId} onChange={(e) => setContentId(e.target.value)}>
              <option value="">Select content…</option>
              {content.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.type})
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label className="field">
            <span>Playlist</span>
            <select value={playlistId} onChange={(e) => setPlaylistId(e.target.value)}>
              <option value="">Select playlist…</option>
              {playlists.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="field-row">
          {entryType === "content" && (
            <label className="field">
              <span>Duration (sec)</span>
              <input
                type="number"
                min={1}
                value={duration}
                onChange={(e) => setDuration(Math.max(1, Number(e.target.value)))}
              />
            </label>
          )}
          <label className="field">
            <span>Priority</span>
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
            />
          </label>
        </div>

        <div className="field">
          <span>Days (none = every day)</span>
          <div className="day-toggles">
            {DAY_LABELS.map((label, d) => (
              <button
                key={d}
                className={"day-toggle" + (days.includes(d) ? " day-toggle--on" : "")}
                onClick={() => toggleDay(d)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="field-row">
          <label className="field">
            <span>Start time</span>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </label>
          <label className="field">
            <span>End time</span>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span>Valid from</span>
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </label>
          <label className="field">
            <span>Valid until (auto-expiry)</span>
            <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </label>
        </div>

        {err && <div className="banner banner--error">{err}</div>}

        <div className="modal-actions">
          <button className="btn btn--ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="btn" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Save entry"}
          </button>
        </div>
      </div>
    </div>
  );
}
